import type { DesktopProfile, ProfilesState } from "@src/electron-api";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

interface DesktopProfilesValue {
	isElectron: boolean;
	loading: boolean;
	profiles: DesktopProfile[];
	currentProfileId: string;
	currentProfile: DesktopProfile | undefined;
	switchProfile: (profileId: string) => Promise<void>;
	createProfile: (name: string) => Promise<DesktopProfile | undefined>;
	deleteProfile: (id: string) => Promise<{ success: boolean; error?: string }>;
	updateProfile: (
		id: string,
		updates: Partial<Omit<DesktopProfile, "id">>,
	) => Promise<DesktopProfile | null>;
}

const EMPTY_PROFILES: DesktopProfile[] = [];

async function noopSwitch(_profileId: string) {}

async function noopCreate(_name: string): Promise<DesktopProfile | undefined> {
	return undefined;
}

async function noopDelete(_id: string) {
	return {
		success: false,
		error: "Electron API not available",
	};
}

async function noopUpdate(
	_id: string,
	_updates: Partial<Omit<DesktopProfile, "id">>,
) {
	return null;
}

const WEB_FALLBACK: DesktopProfilesValue = {
	isElectron: false,
	loading: false,
	profiles: EMPTY_PROFILES,
	currentProfileId: "",
	currentProfile: undefined,
	switchProfile: noopSwitch,
	createProfile: noopCreate,
	deleteProfile: noopDelete,
	updateProfile: noopUpdate,
};

const DesktopProfilesContext = createContext<DesktopProfilesValue | null>(null);
DesktopProfilesContext.displayName = "DesktopProfilesContext";

const PROFILES_UPDATED_EVENT = "illumi-profiles-updated";

function ElectronDesktopProfilesProvider({
	children,
}: {
	children: ReactNode;
}) {
	const electronAPI = window.electronAPI;

	const [state, setState] = useState<ProfilesState>({
		profiles: EMPTY_PROFILES,
		currentProfileId: electronAPI?.currentProfileId || "",
	});
	const [loading, setLoading] = useState(true);

	const refreshProfiles = useCallback(async () => {
		if (!electronAPI?.getProfilesState) return;
		try {
			const nextState = await electronAPI.getProfilesState();
			setState(nextState);
		} catch (err) {
			console.error("Failed to fetch desktop profiles:", err);
		} finally {
			setLoading(false);
		}
	}, [electronAPI]);

	useEffect(() => {
		void refreshProfiles();

		const handleProfilesUpdated = (event: Event) => {
			if (event instanceof CustomEvent && event.detail) {
				setState(event.detail as ProfilesState);
				return;
			}
			void refreshProfiles();
		};

		window.addEventListener(PROFILES_UPDATED_EVENT, handleProfilesUpdated);
		return () => {
			window.removeEventListener(PROFILES_UPDATED_EVENT, handleProfilesUpdated);
		};
	}, [refreshProfiles]);

	const currentProfile = useMemo(() => {
		return (
			state.profiles.find((p) => p.id === state.currentProfileId) ||
			state.profiles[0]
		);
	}, [state.profiles, state.currentProfileId]);

	const switchProfile = useCallback(
		async (profileId: string) => {
			if (!electronAPI?.switchProfile) return;
			await electronAPI.switchProfile(profileId);
		},
		[electronAPI],
	);

	const createProfile = useCallback(
		async (name: string) => {
			if (!electronAPI?.createProfile) return;
			const created = await electronAPI.createProfile(name);
			await refreshProfiles();
			return created;
		},
		[electronAPI, refreshProfiles],
	);

	const deleteProfile = useCallback(
		async (id: string) => {
			if (!electronAPI?.deleteProfile) {
				return { success: false, error: "Electron API not available" };
			}
			const result = await electronAPI.deleteProfile(id);
			await refreshProfiles();
			return result;
		},
		[electronAPI, refreshProfiles],
	);

	const updateProfile = useCallback(
		async (id: string, updates: Partial<Omit<DesktopProfile, "id">>) => {
			if (!electronAPI?.updateProfile) return null;
			return electronAPI.updateProfile(id, updates);
		},
		[electronAPI],
	);

	const value = useMemo<DesktopProfilesValue>(
		() => ({
			isElectron: true,
			loading,
			profiles: state.profiles,
			currentProfileId: state.currentProfileId,
			currentProfile,
			switchProfile,
			createProfile,
			deleteProfile,
			updateProfile,
		}),
		[
			loading,
			state.profiles,
			state.currentProfileId,
			currentProfile,
			switchProfile,
			createProfile,
			deleteProfile,
			updateProfile,
		],
	);

	return (
		<DesktopProfilesContext.Provider value={value}>
			{children}
		</DesktopProfilesContext.Provider>
	);
}

export function DesktopProfilesProvider({
	children,
}: {
	children: ReactNode;
}) {
	if (!window.electronAPI?.isElectron) {
		return children;
	}

	return (
		<ElectronDesktopProfilesProvider>
			{children}
		</ElectronDesktopProfilesProvider>
	);
}

export function useDesktopProfiles(): DesktopProfilesValue {
	const context = useContext(DesktopProfilesContext);
	if (context) {
		return context;
	}
	if (window.electronAPI?.isElectron) {
		throw new Error(
			"useDesktopProfiles must be used within a DesktopProfilesProvider",
		);
	}
	return WEB_FALLBACK;
}
